"use client";

import { useState } from "react";
import { X } from "lucide-react";

const PHONE_RE = /^1[3-9]\d{9}$/;

function WechatIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M9.5 4C5.36 4 2 6.91 2 10.5c0 2.03 1.05 3.83 2.7 5.02L4 18l2.63-1.35c.85.22 1.75.35 2.87.35.28 0 .55-.01.82-.04A5.44 5.44 0 0 1 10 15.5c0-3.31 3.13-6 7-6 .3 0 .6.02.9.05C17.22 7.3 13.7 4 9.5 4Zm-2.6 4.3a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8Zm5.4 0a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8ZM17 10.5c-3.04 0-5.5 2.24-5.5 5s2.46 5 5.5 5c.52 0 1.02-.07 1.5-.19L21 22l-.66-2.04c1.04-.98 1.66-2.32 1.66-3.96 0-2.76-2.46-5.5-5-5.5Zm-1.9 3.2a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5Zm3.8 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5Z" />
    </svg>
  );
}

/** 静态微信二维码占位（本地演示，不接真实微信登录） */
function QrPlaceholder() {
  return (
    <svg viewBox="0 0 96 96" className="h-24 w-24" aria-hidden>
      {Array.from({ length: 12 }).map((_, r) =>
        Array.from({ length: 12 }).map((__, c) => {
          const corner =
            (r < 3 && c < 3) || (r < 3 && c > 8) || (r > 8 && c < 3);
          const cell = corner ? 1 : ((r * 7 + c * 11 + r * c * 3) % 5 === 0 ? 1 : 0);
          return (
            <rect
              key={`${r}-${c}`}
              x={c * 8}
              y={r * 8}
              width={7.4}
              height={7.4}
              rx={1.2}
              className={cell ? "fill-[#1a1b1f]" : "fill-white"}
            />
          );
        }),
      )}
    </svg>
  );
}

export function SignInPage() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [bannerClosed, setBannerClosed] = useState(false);

  const requestCode = async () => {
    if (!PHONE_RE.test(phone)) {
      setError("请输入正确的手机号");
      return;
    }
    setError(null);
    setDevCode(null);
    setSending(true);
    try {
      const res = await fetch("/api/auth/sms-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error ?? "验证码发送失败");
        return;
      }
      // mock 模式下后端直接返回验证码（演示环境不限制发送频率）
      if (data.dev_code) setDevCode(data.dev_code);
    } catch {
      setError("网络异常，请稍后重试");
    } finally {
      setSending(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!PHONE_RE.test(phone)) {
      setError("请输入正确的手机号");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("请输入 6 位验证码");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error ?? "登录失败");
        return;
      }
      // 服务端渲染的路由守卫依赖 cookie，登录成功后整页刷新进入用量页
      window.location.href = "/";
    } catch {
      setError("网络异常，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* 顶部营销横幅 */}
      {!bannerClosed && (
        <div className="flex items-center gap-3 bg-[#3d6aff] px-4 py-2.5 text-sm text-white md:px-10">
          <span className="flex-1">
            多模态视觉理解模型{" "}
            <span className="font-semibold">DeepSeek-V4-Flash-Vision-Exp</span> 已上线 DeepSeek
            API 平台，支持 Chat Completions、Messages、Responses 三种格式调用，欢迎测试和反馈。{" "}
            <a href="#" onClick={(e) => e.preventDefault()} className="font-medium underline underline-offset-2">
              点击查看详情
            </a>
          </span>
          <button
            type="button"
            onClick={() => setBannerClosed(true)}
            className="shrink-0 rounded p-1 text-white/80 hover:bg-white/10 hover:text-white"
            aria-label="关闭公告"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* 主体：左营销区 + 右登录卡片 */}
      <div className="flex flex-1">
        {/* 左侧营销区（lg 以上显示） */}
        <div className="relative hidden flex-[1.15] flex-col justify-center overflow-hidden bg-gradient-to-br from-[#2f4ff0] via-[#3d6aff] to-[#5f86ff] px-14 text-white lg:flex xl:px-20">
          {/* 装饰 */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-4xl font-semibold leading-snug tracking-tight xl:text-[44px]">
              使用 DeepSeek API
              <br />
              构建你的应用
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/85 xl:text-lg">
              基于 DeepSeek V4 系列，创造全新的产品、体验与工作方式
            </p>
            <button
              type="button"
              className="mt-9 inline-flex h-11 items-center justify-center rounded-full bg-white px-7 text-sm font-medium text-[#3d6aff] shadow-sm transition-transform hover:scale-[1.02]"
            >
              查看 API 开发文档
            </button>
          </div>
        </div>

        {/* 右侧登录区 */}
        <div className="flex flex-1 items-center justify-center px-6 py-14">
          <div className="w-full max-w-[520px]">
            {/* 品牌 */}
            <div className="mb-10 flex items-center justify-center gap-2.5">
              <span className="text-[28px] font-bold tracking-tight text-[#1a1b1f]">deepseek</span>
              <span className="rounded-md bg-[#f1f2f4] px-2 py-1 text-xs font-medium text-[#545557]">
                开放平台
              </span>
            </div>

            {/* 表单 + 微信扫码并排 */}
            <div className="flex items-center justify-center gap-10">
              {/* 表单 */}
              <form onSubmit={submit} className="w-[300px] space-y-5" noValidate>
                {/* 手机号 */}
                <div
                  className={`flex items-center rounded-full border bg-white px-5 transition-colors focus-within:border-[#3d6aff] ${
                    error && !phone ? "border-[#f04438]" : "border-[#e3e5e8]"
                  }`}
                >
                  <span className="flex items-center gap-1 py-3 pr-3 text-sm font-medium text-[#1a1b1f]">
                    +86
                    <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3 text-[#9ca3af]" aria-hidden>
                      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <input
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/g, "").slice(0, 11));
                      setError(null);
                    }}
                    placeholder="请输入手机号"
                    inputMode="numeric"
                    autoComplete="tel"
                    className="h-[46px] min-w-0 flex-1 bg-transparent text-sm text-[#1a1b1f] placeholder:text-[#9ca3af] focus:outline-none"
                  />
                </div>

                {/* 验证码 */}
                <div className="flex items-center rounded-full border border-[#e3e5e8] bg-white px-5 transition-colors focus-within:border-[#3d6aff]">
                  <input
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setError(null);
                    }}
                    placeholder="请输入验证码"
                    inputMode="numeric"
                    className="h-[46px] min-w-0 flex-1 bg-transparent text-sm text-[#1a1b1f] placeholder:text-[#9ca3af] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={requestCode}
                    disabled={sending}
                    className="shrink-0 py-3 pl-3 text-sm font-medium text-[#3d6aff] disabled:text-[#b9bdc4]"
                  >
                    发送验证码
                  </button>
                </div>

                {/* 协议文案 */}
                <p className="text-xs leading-relaxed text-[#9ca3af]">
                  注册登录即代表已阅读并同意我们的{" "}
                  <a href="#" onClick={(e) => e.preventDefault()} className="text-[#6b7280] underline underline-offset-2">
                    开放平台协议
                  </a>{" "}
                  与{" "}
                  <a href="#" onClick={(e) => e.preventDefault()} className="text-[#6b7280] underline underline-offset-2">
                    隐私政策
                  </a>
                  。未注册的手机号将自动注册
                </p>

                {/* 错误 / 开发验证码提示 */}
                {error && <p className="text-xs text-[#f04438]">{error}</p>}
                {devCode && (
                  <p className="rounded-lg bg-[#f0f2ff] px-3 py-2 text-xs text-[#4d6bfe]">
                    开发模式：本次验证码为 <span className="font-semibold tracking-wider">{devCode}</span>（见后端控制台）
                  </p>
                )}

                {/* 登录按钮 */}
                <button
                  type="submit"
                  disabled={loading}
                  className="h-[46px] w-full rounded-full bg-[#1a1b1f] text-sm font-medium text-white transition-colors hover:bg-[#2d2f36] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? "登录中..." : "登录"}
                </button>
              </form>

              {/* 微信扫码登录（占位） */}
              <div className="hidden w-[140px] flex-col items-center sm:flex">
                <div className="rounded-xl border border-[#eef0f2] bg-white p-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
                  <QrPlaceholder />
                </div>
                <p className="mt-2.5 flex items-center gap-1.5 text-xs text-[#545557]">
                  <WechatIcon className="h-3.5 w-3.5 text-[#07c160]" />
                  微信扫码登录
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 页脚 */}
      <footer className="flex flex-col items-center gap-1 px-6 py-5 text-xs text-[#b0b3b8] sm:flex-row sm:justify-between">
        <div className="flex items-center gap-4">
          <a href="#" onClick={(e) => e.preventDefault()} className="hover:text-[#7f8287]">
            开放平台协议
          </a>
          <a href="#" onClick={(e) => e.preventDefault()} className="hover:text-[#7f8287]">
            隐私政策
          </a>
        </div>
        <div className="flex items-center gap-4">
          <span>浙ICP备2023025841号</span>
          <a href="#" onClick={(e) => e.preventDefault()} className="hover:text-[#7f8287]">
            联系我们
          </a>
        </div>
      </footer>
    </div>
  );
}
